import axios from "@/plugins/axios.js"
import api from "@/api.js"

const FACE_VALIDATION_PATH = '/api/v1/face/validation'
const REGISTERED_CAMERA_STREAMS_PATH = '/api/v1/trained-face/registered-camera-streams'
const LISTING_DETAILS_PATH = '/api/v1/listing_details/'
const INACTIVE_REASONS = ['face_inactive', 'no_validation_path']
export const ACCESS_TERMINAL_ONLY_MESSAGE = 'Valid for Access Terminal only'

export function extractTrainedFaceEntityId(face = {}){
	const data = face && face.data ? face.data : face
	if (!data) return null
	if (Array.isArray(data.face_trained) && data.face_trained.length) return extractTrainedFaceEntityId(data.face_trained[0])
	if (data.face_trained) return extractTrainedFaceEntityId(data.face_trained)
	if (data.userId) return data.userId
	if (data.faceUUID) return data.faceUUID
	if (data.faceUuid) return data.faceUuid
	if (data.uuid) return data.uuid
	if (data.entity) return data.entity
	if (data.id) return data.id
	return null
}

function normalizeAssignmentId(value){
	if (value === undefined || value === null) return null
	const normalized = String(value).trim()
	return normalized || null
}

function wait(ms){
	return new Promise(resolve => setTimeout(resolve, ms))
}

export function normalizeFaceValidationItem(item = {}){
	const status = item.status
	const reason = item.reason
	const cleanupStatus = item.cleanupStatus || item.cleanup_status
	const isSuccessMessage = item.msg === 'Success'
	const hasAccessTerminalOnlyFlag = item.accessTerminalOnly === true || item.accessTerminalOnly === 1 || item.accessTerminalOnly === '1' || String(item.accessTerminalOnly).toLowerCase() === 'true'
	const isAccessTerminalOnly = Boolean(hasAccessTerminalOnlyFlag || reason === 'access_terminal_fallback')
	const isValid = Boolean(item.bbox || status === 'valid' || item.isValid || isSuccessMessage)
	const isPending = Boolean(status === 'pending' || item.isPending)
	const isInactive = INACTIVE_REASONS.includes(reason) || status === 'unavailable'
	const isInvalid = Boolean(!isValid && !isPending && (status === 'invalid' || isInactive || item.isInvalid || item.msg))
	const messageKey = isInactive ? 'face_analytics_inactive' : isPending ? 'face_validation_pending' : isInvalid ? 'face_photo_invalid' : isValid ? 'face_photo_valid' : undefined

	return {
		...item,
		isValid,
		isInvalid,
		isPending,
		cleanupStatus,
		cleanupPending: isValid && cleanupStatus === 'pending',
		accessTerminalOnly: isAccessTerminalOnly,
		observation: item.observation || (isAccessTerminalOnly ? ACCESS_TERMINAL_ONLY_MESSAGE : undefined),
		messageKey,
	}
}

export function normalizeFaceValidationResponse(response){
	if (!response || !Array.isArray(response.data)) return response

	return {
		...response,
		data: response.data.map(item => normalizeFaceValidationItem(item)),
	}
}

export default new class FaceService{
	constructor(){
		this.listFaces = [];
	}
	async validateTrainingUpload(images){
		const response = await axios.post(`${api.url}${FACE_VALIDATION_PATH}`, images)
		return normalizeFaceValidationResponse(response)
	}
	async startRegisteredCameraStream(deviceId){
		return axios.post(`${api.url}${REGISTERED_CAMERA_STREAMS_PATH}`, { deviceId })
	}
	async captureRegisteredCameraFrame(sessionId){
		return axios.post(`${api.url}${REGISTERED_CAMERA_STREAMS_PATH}/${sessionId}/snapshot`)
	}
	async heartbeatRegisteredCameraStream(sessionId){
		return axios.post(`${api.url}${REGISTERED_CAMERA_STREAMS_PATH}/${sessionId}/heartbeat`)
	}
	async stopRegisteredCameraStream(sessionId){
		return axios.delete(`${api.url}${REGISTERED_CAMERA_STREAMS_PATH}/${sessionId}`)
	}
	async saveTrainedPerson(formData, type = 'user'){
		const endpoint = type === 'user' ? '/api/v1/face/user' : '/api/v1/face/add'
		return axios.post(`${api.url}${endpoint}`, formData)
	}
	async assignTrainedFaceToLists(face, listIds = [], motive = 'Added during enrollment'){
		const entityId = normalizeAssignmentId(extractTrainedFaceEntityId(face))
		if (!entityId) throw new Error('Missing trained face assignment id')
		const ids = Array.from(new Set((listIds || []).map(normalizeAssignmentId).filter(Boolean)))
		return Promise.all(ids.map(listId => axios.post(`${api.url}${LISTING_DETAILS_PATH}`, {
			listId,
			entity: [entityId],
			motive,
			type: 'face',
		})))
	}
	async assignTrainedFaceToListsWithRetry(face, listIds = [], options = {}){
		const retries = Number.isInteger(options.retries) ? options.retries : 2
		const delayMs = Number.isInteger(options.delayMs) ? options.delayMs : 250
		let lastError = null
		for (let attempt = 0; attempt <= retries; attempt++) {
			try{
				const responses = await this.assignTrainedFaceToLists(face, listIds, options.motive)
				return { pending: false, attempts: attempt + 1, responses, listIds }
			}catch(error){
				lastError = error
				if (attempt < retries && delayMs > 0) await wait(delayMs)
			}
		}
		return { pending: true, attempts: retries + 1, error: lastError, face, listIds }
	}
	async retrainTraditionalFace(id){
		return axios.post(`${api.url}/api/v1/face/trained/${id}/retrain`)
	}
	async deleteTrainedUser(userId){
		return axios.delete(`${api.url}/api/v1/face/user/${encodeURIComponent(userId)}`)
	}
	getFaces(){
		return this.listFaces;
	}
	async getList(pg=1, params={}){
		try{
			if (api) {				
				const uri = `${api.url}/api/v1/face/trained/`;// ?page=${pg}					
				const data = await axios.get(uri, { params :{page: pg, ...params }});							
				return data;
			}
		}catch(er){
			console.warn("Error get FaceService ", er)
			return er;
		}
		return null;
	}
	async getListFor(pg=1){		
		try{
			let isloadingReq = true; 
			while(isloadingReq){
				const res =  await this.getList(pg);
				if (res && res.data && res.data.rows.length > 0) {						
					this.listFaces 		= this.listFaces.concat(res.data.rows);// temp_list;					
				}			
				// console.log("valid list ", parseInt(res.data.pages) > pg, parseInt(res.data.pages) , " > ", pg)		
				if (parseInt(res.data.pages) > pg) {
					pg++;				
				}else{
					isloadingReq=false
					// console.log("return list ", this.listFaces)
					return this.listFaces;
				}
			}
			
		}catch(er){
			console.warn("Error get FaceService For ", er)
			return er;
		}
		
	}
}
